<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import axios from "axios";
import apiCall from "@/utils/api";
import { fieldRequired } from "@/utils/input_validation";
import { requestStatus } from "@/utils/constants";
import LabelledField from "@/components/utils/LabelledField.vue";
import LoadingButton from "@/components/utils/LoadingButton.vue";
import LoadingArea from "@/components/utils/LoadingArea.vue";
import BackButton from "@/components/utils/BackButton.vue";
import {
  useVerifyProperty,
  useSendApplicationOtp,
  useValidateApplicationOtp,
  useFetchApplicationFormData,
  useFetchAmalgamationProperties,
  useFetchLandUsePlans,
  useCreateDraft,
  useUpdateDraft,
  useUploadDraftDocument,
  useRemoveDraftDocument,
  useSubmitDraft,
  useLoadDraft,
  preloadedDraft,
} from "./scheme_plan_application_business_logic";

const instance = getCurrentInstance();
const draftIdFromQuery = instance?.proxy?.$route?.query?.draft;

// -- Step navigation --
const pages = {
  VERIFY_PROPERTY: 0,
  OTP_VERIFICATION: 1,
  PROPERTY_DETAILS: 2,
  SCHEME_DETAILS: 3,
  SUCCESS: 4,
};
const currentPage = ref(draftIdFromQuery ? pages.SCHEME_DETAILS : pages.VERIFY_PROPERTY);

const stepItems = ["Verify", "OTP", "Property", "Application", "Complete"];

function stepColor(index) {
  if (currentPage.value > index) return "success";
  if (currentPage.value === index) return "primary";
  return "grey lighten-1";
}

// -- Draft state --
const draftApplication = ref(null); // the current draft record from the server
const isRestoringDraft = ref(false);

// -- Form fields & Stage 4 inputs --
const selectedCategoryId = ref(null);
const selectedTypeId = ref(null);
const remarks = ref("");
const selectedLandUsePlanNo = ref(null);
const numberOfPortions = ref(2);
const portionSizes = ref([null, null]);
const subdivAccessRoadEnabled = ref(false);
const subdivAccessRoadArea = ref(null);
const amalgamAccessRoadEnabled = ref(false);
const amalgamAccessRoadArea = ref(null);
const amalgamSelected = ref(null);
const amalgamSearch = ref("");
const amalgamAdditional = ref([]);
const resultantSize = ref("");
const { creating: creatingDraft, createError: draftCreateError, createDraft } = useCreateDraft();
const { updateDraft } = useUpdateDraft();
const { uploadDocument: uploadDraftDocument } = useUploadDraftDocument();
const { removeDocument: removeDraftDocument } = useRemoveDraftDocument();
const { submitting: submittingDraft, submitError, submitDraft } = useSubmitDraft();
const { loading: loadingDraft, loadDraft } = useLoadDraft();

// Per-document upload state: docId -> { status: 'idle'|'uploading'|'success'|'error', message, appDoc }
const docState = ref({});

function initDocState(docs) {
  const state = {};
  for (const doc of docs) {
    state[doc.id] = { status: "idle", message: "", appDoc: null };
  }
  return state;
}

function applyUploadedDocs(uploadedDocs) {
  for (const appDoc of uploadedDocs) {
    const docId = appDoc.scheme_plan_supporting_document_id;
    if (docState.value[docId]) {
      docState.value[docId].status = "success";
      docState.value[docId].appDoc = appDoc;
    }
  }
}

async function restoreDraftData(draft) {
  isRestoringDraft.value = true;
  try {
    draftApplication.value = draft;
    // Restore property pseudo-data so the property summary renders
    verifiedData.value = { property: draft.scheme_plan_property };
    
    // Restore client id number / pn
    if (draft.client && draft.client.id_number) {
      idNumber.value = draft.client.id_number;
    }
    if (draft.scheme_plan_property && draft.scheme_plan_property.pn) {
      pn.value = draft.scheme_plan_property.pn;
    }
    
    // Restore form fields
    if (draft.scheme_plan_category_id) selectedCategoryId.value = draft.scheme_plan_category_id;
    if (draft.scheme_plan_type_id) selectedTypeId.value = draft.scheme_plan_type_id;
    if (draft.remarks) remarks.value = draft.remarks;
    
    // Restore subdivision / amalgamation
    const appDataRaw = draft.application_data ?? {};
    const appData = typeof appDataRaw === "string" ? JSON.parse(appDataRaw || "{}") : appDataRaw;
    const subdivisionData =
      appData?.type === "subdivision"
        ? appData
        : appData?.subdivision_data && typeof appData.subdivision_data === "object"
          ? appData.subdivision_data
          : appData;
    // Some older / partial drafts may not have `type` set on application_data,
    // but will still have `portions` populated. In that case, still restore
    // the subdivision portions from the saved data.
    if (Array.isArray(subdivisionData?.portions) && subdivisionData.portions.length) {
      numberOfPortions.value = subdivisionData.portions.length;
      portionSizes.value = subdivisionData.portions.map((p) => p.size_sqm);
      if (subdivisionData.access_road_area) {
        subdivAccessRoadEnabled.value = true;
        subdivAccessRoadArea.value = subdivisionData.access_road_area;
      }
    }
    if (appData.type === "amalgamation" && appData.property_ids) {
      resultantSize.value = String(appData.resultant_size ?? "");
      if (appData.access_road_area) {
        amalgamAccessRoadEnabled.value = true;
        amalgamAccessRoadArea.value = appData.access_road_area;
      }
      if (idNumber.value && draft.scheme_plan_property) {
        await fetchAmalgamationProperties({
          idNumber: idNumber.value,
          excludeId: draft.scheme_plan_property.id,
        });
        amalgamAdditional.value = amalgamProperties.value.filter((p) =>
          appData.property_ids.includes(p.id)
        );
      }
    }
    if (appData.land_use_plan_no) {
      selectedLandUsePlanNo.value = appData.land_use_plan_no;
    }
  } finally {
    isRestoringDraft.value = false;
  }
}

// -- Resume from draft (query param ?draft=ID) --
onMounted(async () => {
  const draftId = instance?.proxy?.$route?.query?.draft;
  if (draftId) {
    try {
      let draft = preloadedDraft.value;
      if (draft && draft.id == draftId) {
        preloadedDraft.value = null;
      } else {
        draft = await loadDraft(draftId);
      }
      if (draft && draft.status === "Draft") {
        await restoreDraftData(draft);
        // Fetch form data then init doc state
        await fetchFormData();
        docState.value = initDocState(supportingDocuments.value);
        applyUploadedDocs(draft.documents ?? []);
        currentPage.value = pages.SCHEME_DETAILS;
      } else {
        currentPage.value = pages.VERIFY_PROPERTY;
      }
    } catch (_) {
      currentPage.value = pages.VERIFY_PROPERTY;
    }
  }
});

// -- Step 1: Verify Property --
const verifyForm = ref(null);
const idNumber = ref("");
const pn = ref("");
const { verifying, verifiedData, verifyError, verifyProperty } =
  useVerifyProperty();

async function onVerifyClicked() {
  if (!verifyForm.value.validate()) return;
  try {
    await verifyProperty({ pn: pn.value, idNumber: idNumber.value });
    currentPage.value = pages.OTP_VERIFICATION;
    sendOtp({ idNumber: idNumber.value });
  } catch (_) {}
}

// -- Step 2: OTP Verification --
const { otpData, sendOtp, resetOtp, sendingOtp, resendDisabled } =
  useSendApplicationOtp();
const { validating, otpInvalid, validateOtp, resetOtpStatus } =
  useValidateApplicationOtp();
const otpForm = ref(null);

async function onSendOtpClicked() {
  resetOtpStatus();
  sendOtp({ idNumber: idNumber.value });
}

async function onVerifyOtpClicked() {
  if (!otpForm.value.validate()) return;
  try {
    await validateOtp({ idNumber: idNumber.value, otp: otpData.otpModel });
    currentPage.value = pages.PROPERTY_DETAILS;
  } catch (_) {}
}

function onBackToVerify() {
  currentPage.value = pages.VERIFY_PROPERTY;
  resetOtp();
  resetOtpStatus();
}

// -- Step 3: Property Details --
async function onCreateApplicationClicked() {
  // Create draft first — if an application is already in progress the API returns
  // 422 and we stay on step 3 to show the error rather than landing on step 4.
  await fetchFormData();
  try {
    const draft = await createDraft({ propertyId: verifiedData.value.property.id });
    await restoreDraftData(draft);
    // Navigate to step 4 only after a successful draft creation
    currentPage.value = pages.SCHEME_DETAILS;
    docState.value = initDocState(supportingDocuments.value);
    // If draft already has docs uploaded, restore them
    applyUploadedDocs(draft.documents ?? []);
  } catch (_) {
    // draftCreateError is set by useCreateDraft — shown in step 3 template
  }
}

function onBackToOtp() {
  currentPage.value = pages.OTP_VERIFICATION;
}

// -- Step 4: Scheme Details --
const { formData, loading: formDataLoading, fetchFormData } =
  useFetchApplicationFormData();

const schemeForm = ref(null);
const createdApplication = ref(null);

const categories = computed(() => formData.value?.categories || []);
const types = computed(() => formData.value?.types || []);
const supportingDocuments = computed(
  () => formData.value?.supportingDocuments || []
);

// Keep docState in sync when supportingDocuments loads
watch(supportingDocuments, (docs) => {
  if (docs.length && !Object.keys(docState.value).length) {
    docState.value = initDocState(docs);
  }
});

// Index of the first document not yet successfully uploaded
const currentDocIndex = computed(() => {
  const docs = supportingDocuments.value;
  for (let i = 0; i < docs.length; i++) {
    const s = docState.value[docs[i].id];
    if (!s || s.status !== 'success') return i;
  }
  return docs.length; // all uploaded
});

// Auto-save draft fields on change (debounced)
let _autoSaveTimer = null;
function scheduleDraftSave() {
  if (!draftApplication.value) return;
  clearTimeout(_autoSaveTimer);
  _autoSaveTimer = setTimeout(() => {
    // Construct application_data
    let appData = {};
    if (isSubdivision.value) {
      appData = {
        type: "subdivision",
        portions: portionSizes.value.map((size, i) => ({
          label: portionLabel(i),
          size_sqm: size !== null && size !== "" ? parseFloat(size) : null,
        })),
        access_road_area: subdivAccessRoadEnabled.value && subdivAccessRoadArea.value !== null && subdivAccessRoadArea.value !== ""
          ? parseFloat(subdivAccessRoadArea.value)
          : null,
      };
    } else if (isAmalgamation.value) {
      appData = {
        type: "amalgamation",
        property_ids: amalgamAdditional.value.map((p) => p.id),
        resultant_size: resultantSize.value !== null && resultantSize.value !== "" ? parseFloat(resultantSize.value) : null,
        access_road_area: amalgamAccessRoadEnabled.value && amalgamAccessRoadArea.value !== null && amalgamAccessRoadArea.value !== ""
          ? parseFloat(amalgamAccessRoadArea.value)
          : null,
      };
    }

    if (selectedLandUsePlanNo.value) {
      appData.land_use_plan_no = selectedLandUsePlanNo.value;
    }

    const fields = {
      scheme_plan_category_id: selectedCategoryId.value || null,
      scheme_plan_type_id: selectedTypeId.value || null,
      remarks: remarks.value || null,
      application_data: Object.keys(appData).length ? appData : null,
    };
    updateDraft(draftApplication.value.id, fields);
  }, 1200);
}

watch(
  [
    selectedCategoryId,
    selectedTypeId,
    remarks,
    selectedLandUsePlanNo,
    numberOfPortions,
    portionSizes,
    subdivAccessRoadEnabled,
    subdivAccessRoadArea,
    amalgamAdditional,
    resultantSize,
    amalgamAccessRoadEnabled,
    amalgamAccessRoadArea,
  ],
  () => {
    if (!isRestoringDraft.value) {
      scheduleDraftSave();
    }
  },
  { deep: true }
);

// -- Type detection --
const selectedType = computed(
  () => types.value.find((t) => t.id === selectedTypeId.value) ?? null
);
const isSubdivision = computed(
  () => selectedType.value?.name?.toLowerCase() === "subdivision"
);
const isAmalgamation = computed(
  () => selectedType.value?.name?.toLowerCase() === "amalgamation"
);

// -- Land Use Plan --
const { landUsePlans, loading: loadingLandUsePlans, fetchLandUsePlans } = useFetchLandUsePlans();

function parseAdjudication(lrNumber) {
  if (!lrNumber) return null;
  const parts = lrNumber.split(' ');
  return parts.length > 1 ? parts.slice(0, -1).join(' ') : null;
}

const allAdjudicationNames = computed(() => {
  if (!verifiedData.value) return [];
  const motherAdj = parseAdjudication(verifiedData.value.property.lr_number);
  const extraAdjs = isAmalgamation.value
    ? amalgamAdditional.value.map((p) => parseAdjudication(p.lr_number))
    : [];
  return [...new Set([motherAdj, ...extraAdjs].filter(Boolean))];
});

watch(allAdjudicationNames, (names) => {
  if (!isRestoringDraft.value) {
    selectedLandUsePlanNo.value = null;
  }
  fetchLandUsePlans(names);
}, { deep: true });

// -- Step 4 extra: Subdivision --
function portionLabel(i) {
  if (i < 26) return String.fromCharCode(65 + i);
  return 'A' + (i - 25);
}

watch(numberOfPortions, (newVal) => {
  const n = Math.max(2, parseInt(newVal) || 2);
  const current = portionSizes.value.length;
  if (n > current) {
    for (let i = current; i < n; i++) portionSizes.value.push(null);
  } else {
    portionSizes.value.splice(n);
  }
});
const motherSqm = computed(() =>
  parseFloat(verifiedData.value?.property?.size_sqm || 0)
);
const portionTotal = computed(() =>
  portionSizes.value.reduce((s, v) => s + (parseFloat(v) || 0), 0)
);


const subdivTotalWithRoad = computed(() => {
  const road = subdivAccessRoadEnabled.value ? (parseFloat(subdivAccessRoadArea.value) || 0) : 0;
  return portionTotal.value + road;
});
const portionRemaining = computed(() => motherSqm.value - subdivTotalWithRoad.value);
const portionSizeValid = computed(() => Math.abs(portionRemaining.value) < 0.01);

// -- Step 4 extra: Amalgamation --
const { amalgamLoading, amalgamProperties, fetchAmalgamationProperties } =
  useFetchAmalgamationProperties();


const amalgamTotal = computed(
  () =>
    motherSqm.value +
    amalgamAdditional.value.reduce((s, p) => s + parseFloat(p.size_sqm || 0), 0)
);
const resultantSizeNum = computed(() => parseFloat(resultantSize.value) || 0);
const amalgamAccessRoadAreaNum = computed(() =>
  amalgamAccessRoadEnabled.value ? (parseFloat(amalgamAccessRoadArea.value) || 0) : 0
);
const resultantSizeValid = computed(
  () => resultantSizeNum.value > 0 && (resultantSizeNum.value + amalgamAccessRoadAreaNum.value) <= amalgamTotal.value + 0.001
);
const amalgamSizeExceeded = computed(
  () => resultantSizeNum.value > 0 && (resultantSizeNum.value + amalgamAccessRoadAreaNum.value) > amalgamTotal.value + 0.001
);

watch(amalgamTotal, (newTotal) => {
  if (isAmalgamation.value) {
    resultantSize.value = newTotal > 0 ? String(newTotal) : "";
  }
});

watch(selectedTypeId, (newVal, oldVal) => {
  if (isRestoringDraft.value) return;
  // Prevent wiping restored subdivision data during initial model hydration.
  if (oldVal == null) return;
  if (newVal === oldVal) return;
  portionSizes.value = [null, null];
  numberOfPortions.value = 2;
  amalgamAdditional.value = [];
  resultantSize.value = "";
  amalgamSelected.value = null;
  subdivAccessRoadEnabled.value = false;
  subdivAccessRoadArea.value = null;
  amalgamAccessRoadEnabled.value = false;
  amalgamAccessRoadArea.value = null;
  selectedLandUsePlanNo.value = null;
});

watch(amalgamSearch, (val) => {
  if (isAmalgamation.value && verifiedData.value && val && val.length >= 2) {
    fetchAmalgamationProperties({
      idNumber: idNumber.value,
      excludeId: verifiedData.value.property.id,
    });
  }
});

function addAmalgamProperty(prop) {
  if (!prop) return;
  if (prop.id === verifiedData.value?.property?.id) {
    alert("The property being amalgamated cannot be chosen again.");
    amalgamSelected.value = null;
    return;
  }
  if (prop.client_id && verifiedData.value?.property?.client_id && prop.client_id !== verifiedData.value.property.client_id) {
    alert("Property " + prop.pn + " does not belong to the same client.");
    amalgamSelected.value = null;
    return;
  }
  if (prop.workflow_status && prop.workflow_status.code && prop.workflow_status.code !== 'ACT') {
    alert("Property " + prop.pn + " must be Active to be amalgamated.");
    amalgamSelected.value = null;
    return;
  }
  if (!amalgamAdditional.value.find((p) => p.id === prop.id)) {
    amalgamAdditional.value.push(prop);
  }
  amalgamSelected.value = null;
}

function removeAmalgamProperty(id) {
  amalgamAdditional.value = amalgamAdditional.value.filter((p) => p.id !== id);
}

// -- Document Upload (per-doc auto-upload) --
async function onDocumentFileSelected(docId, file) {
  if (!file || !draftApplication.value) return;
  docState.value[docId].status = "uploading";
  docState.value[docId].message = "";
  try {
    const appDoc = await uploadDraftDocument(draftApplication.value.id, docId, file);
    docState.value[docId].status = "success";
    docState.value[docId].appDoc = appDoc;
  } catch (error) {
    docState.value[docId].status = "error";
    docState.value[docId].message =
      error?.response?.data?.message || "Upload failed. Please try again.";
  }
}

async function onDocumentRemove(docId) {
  const state = docState.value[docId];
  if (!state?.appDoc || !draftApplication.value) return;
  state.status = "uploading"; // reuse spinner while deleting
  try {
    await removeDraftDocument(draftApplication.value.id, state.appDoc.id);
    state.status = "idle";
    state.appDoc = null;
    state.message = "";
  } catch (_) {
    state.status = "success"; // revert on failure
  }
}

const downloadingDocId = ref(null);

function triggerBlobDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

async function onDocumentDownload(appDoc) {
  if (!appDoc || !draftApplication.value) return;
  downloadingDocId.value = appDoc.id;
  try {
    const blob = await apiCall({
      url: `/api/client/scheme-plan-application/${draftApplication.value.id}/document/${appDoc.id}`,
      method: 'GET',
      responseType: 'blob',
    });
    triggerBlobDownload(blob, appDoc.original_name || 'document.pdf');
  } catch (e) {
    console.error('Failed to download document', e);
  } finally {
    downloadingDocId.value = null;
  }
}

// -- Submit --
async function onSubmitApplication() {
  if (!schemeForm.value.validate()) return;
  if (amalgamSizeExceeded.value) return;

  // Build form payload (same fields as old store, but sent to submitDraft)
  const payload = new FormData();
  payload.append("scheme_plan_category_id", selectedCategoryId.value);
  payload.append("scheme_plan_type_id", selectedTypeId.value);
  if (remarks.value) payload.append("remarks", remarks.value);
  if (selectedLandUsePlanNo.value) payload.append("land_use_plan_no", selectedLandUsePlanNo.value);

  if (isSubdivision.value) {
    payload.append(
      "subdivision_data",
      JSON.stringify({
        portions: portionSizes.value.map((size, i) => ({
          label: portionLabel(i),
          size_sqm: parseFloat(size),
        })),
        access_road_area: subdivAccessRoadEnabled.value ? (parseFloat(subdivAccessRoadArea.value) || 0) : 0,
      })
    );
  }
  if (isAmalgamation.value) {
    payload.append(
      "amalgamation_data",
      JSON.stringify({
        property_ids: amalgamAdditional.value.map((p) => p.id),
        resultant_size: parseFloat(resultantSize.value),
        access_road_area: amalgamAccessRoadEnabled.value ? (parseFloat(amalgamAccessRoadArea.value) || 0) : 0,
      })
    );
  }

  try {
    const response = await submitDraft(draftApplication.value.id, payload);
    createdApplication.value = response;
    currentPage.value = pages.SUCCESS;
  } catch (_) {}
}

function onBackToPropertyDetails() {
  currentPage.value = pages.PROPERTY_DETAILS;
}

// -- Document Viewer (title deed in step 3) --
const docViewerDialog = ref(false);
const docLoading = ref(false);
const viewerKey = ref(0);
const viewingDoc = ref({ pn: "", url: "", downloadUrl: "", filename: "", isPdf: false });
let _currentBlobUrl = null;

function _revokeBlobUrl() {
  if (_currentBlobUrl) { URL.revokeObjectURL(_currentBlobUrl); _currentBlobUrl = null; }
}

watch(docViewerDialog, (isOpen) => {
  if (!isOpen) {
    _revokeBlobUrl();
    viewingDoc.value = { pn: "", url: "", downloadUrl: "", filename: "", isPdf: false };
  }
});

async function viewDocument(item) {
  if (!item.title_deed_scan_url) return;
  const apiUrl = `${process.env.VUE_APP_API_URL}/api/client/scheme-plan-property/${item.id}/title-deed`;
  const downloadUrl = `${apiUrl}?download=1`;
  const ext = item.title_deed_scan_url.split('?')[0].split('.').pop().toLowerCase();
  const isPdf = ext === 'pdf';
  const filename = item.title_deed_scan_url.split('/').pop().split('?')[0] || `title-deed-${item.pn}`;
  _revokeBlobUrl();
  viewerKey.value++;
  viewingDoc.value = { pn: item.pn, url: "", downloadUrl, filename, isPdf };
  docLoading.value = true;
  docViewerDialog.value = true;
  try {
    const response = await axios.get(apiUrl, {
      responseType: "blob",
      headers: { Authorization: localStorage.getItem("user-token") },
    });
    const blobUrl = URL.createObjectURL(response.data);
    _currentBlobUrl = blobUrl;
    viewingDoc.value = { ...viewingDoc.value, url: blobUrl };
  } catch (e) {
    console.error("Failed to load title deed document", e);
  } finally {
    docLoading.value = false;
  }
}

async function downloadDocument() {
  const { downloadUrl, filename } = viewingDoc.value;
  if (!downloadUrl) return;
  try {
    const response = await axios.get(downloadUrl, {
      responseType: "blob",
      headers: { Authorization: localStorage.getItem("user-token") },
    });
    const url = URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error("Failed to download title deed document", e);
  }
}

function formatNumber(value) {
  if (value == null) return "N/A";
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function onStartNewApplication() {
  currentPage.value = pages.VERIFY_PROPERTY;
  idNumber.value = ""; pn.value = "";
  verifiedData.value = null; verifyError.value = null;
  resetOtp(); resetOtpStatus();
  selectedCategoryId.value = null; selectedTypeId.value = null;
  remarks.value = "";
  portionSizes.value = [null, null]; numberOfPortions.value = 2;
  amalgamAdditional.value = []; resultantSize.value = "";
  amalgamSelected.value = null;
  subdivAccessRoadEnabled.value = false; subdivAccessRoadArea.value = null;
  amalgamAccessRoadEnabled.value = false; amalgamAccessRoadArea.value = null;
  selectedLandUsePlanNo.value = null; landUsePlans.value = [];
  draftApplication.value = null; docState.value = {};
  createdApplication.value = null;
  // Clear draft query param
  instance?.proxy?.$router?.replace({ query: {} });
}
</script>

<template>
  <v-container fluid class="pa-4 pt-10">
    <!-- Loading Draft Indicator -->
    <v-row v-if="loadingDraft" justify="center" align="center" style="height: 60vh">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary" size="64" class="mb-4" />
        <div class="text-subtitle-1 font-weight-medium grey--text text--darken-2">
          Loading draft application, please wait...
        </div>
      </v-col>
    </v-row>

    <template v-else>
      <!-- Header -->
      <v-row justify="center">
        <v-col cols="12" md="8" lg="7">
          <div class="text-h5 font-weight-bold primary--text mb-1">
            <v-icon color="primary" class="mr-1" style="margin-top: -3px">mdi-file-document-edit-outline</v-icon>
            Scheme Plan Application
          </div>
          <div class="text-body-2 grey--text text--darken-1 mb-5">
            Complete the steps below to submit your application.
          </div>
        </v-col>
      </v-row>

    <!-- Step Indicator -->
    <v-row justify="center" class="mb-2">
      <v-col cols="12" md="8" lg="7">
        <div class="d-flex align-center justify-space-between step-indicator">
          <div
            v-for="(step, i) in stepItems"
            :key="i"
            class="d-flex align-center"
            :class="{ 'flex-grow-1': i < stepItems.length - 1 }"
          >
            <!-- Step circle -->
            <div class="d-flex flex-column align-center" style="min-width: 40px">
              <v-avatar
                :size="36"
                :color="stepColor(i)"
                class="step-circle elevation-1"
              >
                <v-icon v-if="currentPage > i" color="white" small>mdi-check</v-icon>
                <span v-else class="white--text text-body-2 font-weight-bold">{{ i + 1 }}</span>
              </v-avatar>
              <span
                class="text-caption mt-1 text-center"
                :class="currentPage === i ? 'primary--text font-weight-bold' : 'grey--text'"
                style="max-width: 80px; line-height: 1.2"
              >
                {{ step }}
              </span>
            </div>
            <!-- Connector line -->
            <v-divider
              v-if="i < stepItems.length - 1"
              class="mx-2 mb-5"
              :class="currentPage > i ? 'primary' : ''"
              :style="{ borderColor: currentPage > i ? 'var(--v-primary-base)' : undefined }"
            />
          </div>
        </div>
      </v-col>
    </v-row>

    <v-tabs-items v-model="currentPage" touchless>
      <!-- Step 1: Verify Property -->
      <v-tab-item :value="pages.VERIFY_PROPERTY">
        <v-row justify="center">
          <v-col cols="12" md="7" lg="6">
            <v-card class="pa-7 rounded-lg" elevation="2">
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" class="mr-2">mdi-home-search-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Verify Property Ownership</span>
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-5">
                Enter the customer's ID number and either the Property Number (PN) or
                Title Deed PN to verify ownership before proceeding.
              </div>
              <v-form ref="verifyForm" @submit.prevent="onVerifyClicked">
                <LabelledField label="ID Number" required>
                  <v-text-field
                    class="text-field-background"
                    outlined
                    dense
                    v-model="idNumber"
                    :rules="[fieldRequired]"
                    hide-details="auto"
                  />
                </LabelledField>

                <LabelledField label="Property (PN), Title PN or LR Number" required class="mt-4">
                  <v-text-field
                    class="text-field-background"
                    outlined
                    dense
                    v-model="pn"
                    :rules="[fieldRequired]"
                    hide-details="auto"
                  />
                </LabelledField>

                <v-alert
                  v-if="verifyError"
                  type="error"
                  dense
                  text
                  class="mt-4"
                >
                  {{ verifyError }}
                </v-alert>

                <div class="mt-6">
                  <LoadingButton
                    block
                    color="primary"
                    :loading="verifying"
                    type="submit"
                    class="text-none"
                  >
                    <v-icon left small>mdi-magnify</v-icon>
                    Verify Property
                    <template #loadingText>Verifying...</template>
                  </LoadingButton>
                </div>
              </v-form>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Step 2: OTP Verification -->
      <v-tab-item :value="pages.OTP_VERIFICATION">
        <v-row justify="center">
          <v-col cols="12" md="8" lg="8">
            <v-card class="pa-7 rounded-lg" elevation="2">
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" class="mr-2">mdi-shield-lock-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold">OTP Verification</span>
              </div>

              <!-- Verified property summary -->
              <template v-if="verifiedData">
                <v-card flat class="pa-3 mb-4 rounded" color="green lighten-5">
                  <div class="d-flex align-center">
                    <v-icon color="success" class="mr-2" small>mdi-check-circle</v-icon>
                    <span class="text-body-2">
                      Property <strong>{{ verifiedData.property.pn }}</strong>
                      verified for
                      <strong>{{ verifiedData.client_name }}</strong>
                    </span>
                  </div>
                  <div class="ml-6 mt-1 text-body-2 grey--text text--darken-2">
                    LR: {{ verifiedData.property.lr_number }}
                    <template v-if="verifiedData.obscured_phone">
                      &bull; Phone: {{ verifiedData.obscured_phone }}
                    </template>
                    <template v-if="verifiedData.obscured_email">
                      &bull; Email: {{ verifiedData.obscured_email }}
                    </template>
                  </div>
                </v-card>
              </template>

              <div class="text-body-2 grey--text text--darken-1 mb-4">
                A verification code has been sent to the customer's phone number and
                email address. Please enter it below.
              </div>

              <v-form ref="otpForm" @submit.prevent="onVerifyOtpClicked">
                <LabelledField label="Verification Code" required>
                  <v-text-field
                    class="text-field-background"
                    outlined
                    dense
                    v-model="otpData.otpModel"
                    :rules="[fieldRequired]"
                    hide-details="auto"
                    placeholder="Enter 6-digit code"
                  />
                </LabelledField>

                <v-alert
                  v-if="otpInvalid"
                  type="error"
                  dense
                  text
                  class="mt-4"
                >
                  The verification code is invalid. Please try again.
                </v-alert>

                <div class="mt-6 d-flex">
                  <LoadingButton
                    color="primary"
                    :loading="validating"
                    type="submit"
                    class="text-none mr-2"
                  >
                    <v-icon left small>mdi-check</v-icon>
                    Verify Code
                    <template #loadingText>Verifying...</template>
                  </LoadingButton>

                  <v-btn
                    outlined
                    color="grey darken-1"
                    class="text-none"
                    :disabled="resendDisabled || validating"
                    :loading="sendingOtp"
                    @click="onSendOtpClicked"
                  >
                    <v-icon left small>mdi-refresh</v-icon>
                    <template v-if="otpData.resendOtpTimeout > 0">
                      Resend ({{ otpData.resendOtpTimeout }}s)
                    </template>
                    <template v-else>Resend Code</template>
                  </v-btn>
                </div>
              </v-form>

              <v-divider class="my-5" />
              <div class="d-flex justify-space-between">
                <v-btn
                  color="warning"
                  class="text-none"
                  @click="onBackToVerify"
                >
                  <v-icon left small>mdi-arrow-left</v-icon>
                  Back
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Step 3: Property Details -->
      <v-tab-item :value="pages.PROPERTY_DETAILS">
        <v-row justify="center">
          <v-col cols="12" md="12" lg="10">
            <v-card class="pa-7 rounded-lg" elevation="2">
              <div class="d-flex align-center mb-4">
                <v-icon color="primary" class="mr-2">mdi-home-city-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Property Details</span>
              </div>

              <template v-if="verifiedData">
                <v-row>
                  <!-- Left Column -->
                  <v-col cols="12" md="6">
                    <v-simple-table dense>
                      <template #default>
                        <tbody>
                          <tr>
                            <td class="font-weight-bold">PN</td>
                            <td>{{ verifiedData.property.pn }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Adjudication</td>
                            <td>{{ verifiedData.property.lr_number ? verifiedData.property.lr_number.split(' ').slice(0, -1).join(' ') : 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Parcel No.</td>
                            <td>{{ verifiedData.property.lr_number ? verifiedData.property.lr_number.split(' ').slice(-1)[0] : 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Title Deed PN</td>
                            <td>{{ verifiedData.property.title_deed_pn || 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Owner</td>
                            <td>
                              <template v-if="verifiedData.owners && verifiedData.owners.length">
                                <div v-for="(owner, i) in verifiedData.owners" :key="i" :class="{ 'mt-1': i > 0 }">
                                  {{ owner.name }}
                                  <v-chip v-if="owner.is_primary" x-small color="amber darken-2" dark class="ml-1 font-weight-bold">Primary</v-chip>
                                </div>
                              </template>
                              <template v-else>{{ verifiedData.client_name }}</template>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Size (m²)</td>
                            <td>{{ formatNumber(verifiedData.property.size_sqm) }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Source</td>
                            <td>{{ verifiedData.property.source || 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Title Deed</td>
                            <td>
                              <v-icon
                                small
                                :color="verifiedData.property.title_deed_scan_url ? 'success' : 'grey lighten-1'"
                              >
                                {{ verifiedData.property.title_deed_scan_url ? 'mdi-file-check' : 'mdi-file-remove-outline' }}
                              </v-icon>
                              <span class="ml-1">
                                {{ verifiedData.property.title_deed_scan_url ? 'Present' : 'Absent' }}
                              </span>
                              <v-btn
                                v-if="verifiedData.property.title_deed_scan_url"
                                text
                                small
                                class="primary--text text-none ml-1"
                                @click="viewDocument(verifiedData.property)"
                              >
                                View Document
                              </v-btn>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-col>

                  <!-- Right Column -->
                  <v-col cols="12" md="6">
                    <v-simple-table dense>
                      <template #default>
                        <tbody>
                          <tr>
                            <td class="font-weight-bold">SubCounty</td>
                            <td>{{ verifiedData.property.sub_county ? verifiedData.property.sub_county.sub_county_name : 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Ward</td>
                            <td>{{ verifiedData.property.ward ? verifiedData.property.ward.ward_name : 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Town</td>
                            <td>{{ verifiedData.property.town ? verifiedData.property.town.town_name : 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Latitude</td>
                            <td>{{ verifiedData.property.latitude || 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Longitude</td>
                            <td>{{ verifiedData.property.longitude || 'N/A' }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-col>
                </v-row>
              </template>

              <v-divider class="my-4" />
              <v-alert
                v-if="draftCreateError"
                type="error"
                dense
                text
                class="mb-4"
              >
                {{ draftCreateError }}
              </v-alert>
              <div class="d-flex justify-space-between">
                <v-btn
                  color="warning"
                  class="text-none"
                  @click="onBackToOtp"
                >
                  <v-icon left small>mdi-arrow-left</v-icon>
                  Back
                </v-btn>
                <v-btn
                  color="primary"
                  class="text-none"
                  :loading="creatingDraft"
                  :disabled="creatingDraft"
                  @click="onCreateApplicationClicked"
                >
                  Create Application
                  <v-icon right small>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Document Viewer Dialog -->
        <v-dialog
          v-model="docViewerDialog"
          fullscreen
          transition="dialog-bottom-transition"
        >
          <v-card>
            <v-toolbar dense color="primary" dark>
              <v-toolbar-title>
                Title Deed — {{ viewingDoc.pn }}
              </v-toolbar-title>
              <v-spacer />
              <v-btn
                icon
                :disabled="docLoading"
                title="Download"
                @click="downloadDocument"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
              <v-btn icon @click="docViewerDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pa-2">
              <div v-if="docLoading" class="d-flex justify-center align-center" style="height: 80vh">
                <v-progress-circular indeterminate color="primary" size="64" />
              </div>
              <div v-else align="center">
                <iframe
                  v-if="viewingDoc.isPdf"
                  :key="'pdf-' + viewerKey"
                  :src="viewingDoc.url"
                  style="width: 100%; height: 85vh; border: none"
                ></iframe>
                <v-img
                  v-else
                  :key="'img-' + viewerKey"
                  :src="viewingDoc.url"
                  contain
                  max-height="90vh"
                />
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-tab-item>

      <!-- Step 4: Scheme Details Form -->
      <v-tab-item :value="pages.SCHEME_DETAILS">
        <v-row justify="center">
          <v-col cols="12" md="10" lg="10">
            <v-card class="pa-7 rounded-lg" elevation="2">
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" class="mr-2">mdi-clipboard-text-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Scheme Plan Details</span>
              </div>

              <LoadingArea :loading="formDataLoading">
                <v-form
                  ref="schemeForm"
                  @submit.prevent="onSubmitApplication"
                >
                  <!-- Property summary -->
                  <v-card
                    v-if="verifiedData"
                    flat
                    class="pa-3 mb-4 rounded"
                    color="blue lighten-5"
                  >
                    <div class="d-flex align-center">
                      <v-icon color="info" class="mr-2" small>mdi-information</v-icon>
                      <span class="text-body-2">
                        Property:
                        <strong>{{ verifiedData.property.pn }}</strong> —
                        {{ verifiedData.property.lr_number }}
                      </span>
                    </div>
                  </v-card>

                  <!-- Draft saved / error indicator -->
                  <v-alert
                    v-if="draftCreateError"
                    type="error" dense text class="mb-4"
                    icon="mdi-alert-circle-outline"
                  >
                    {{ draftCreateError }}
                  </v-alert>
                  <v-alert
                    v-else-if="draftApplication"
                    type="success" dense text class="mb-4"
                    icon="mdi-content-save-check-outline"
                  >
                    Draft <strong>{{ draftApplication.application_number }}</strong> is saved.
                    Your uploaded documents and field entries are preserved — you can leave and return anytime.
                  </v-alert>
                  <v-alert
                    v-else-if="creatingDraft"
                    type="info" dense text class="mb-4"
                    icon="mdi-loading"
                  >
                    Creating draft…
                  </v-alert>

                  <!-- Block the form entirely when draft creation failed -->
                  <div v-if="draftCreateError" class="text-center mt-4">
                    <v-btn color="warning" class="text-none" @click="onBackToPropertyDetails">
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Back to Property Details
                    </v-btn>
                  </div>

                  <template v-if="!draftCreateError">
                  <v-row>
                    <v-col cols="12" md="4">
                      <LabelledField label="Scheme Plan Type" required>
                        <v-autocomplete
                          class="text-field-background"
                          outlined
                          dense
                          v-model="selectedTypeId"
                          :items="types"
                          item-text="name"
                          item-value="id"
                          :rules="[fieldRequired]"
                          hide-details="auto"
                          placeholder="Select type..."
                        />
                      </LabelledField>
                    </v-col>
                    <v-col cols="12" md="4">
                      <LabelledField label="Scheme Plan Category" required>
                        <v-autocomplete
                          class="text-field-background"
                          outlined
                          dense
                          v-model="selectedCategoryId"
                          :items="categories"
                          item-text="name"
                          item-value="id"
                          :rules="[fieldRequired]"
                          hide-details="auto"
                          placeholder="Select category..."
                        />
                      </LabelledField>
                    </v-col>
                    <v-col cols="12" md="4">
                      <LabelledField label="Land Use Plan Number">
                        <v-autocomplete
                          class="text-field-background"
                          outlined
                          dense
                          v-model="selectedLandUsePlanNo"
                          :items="landUsePlans"
                          item-text="plan_number"
                          item-value="plan_number"
                          :loading="loadingLandUsePlans"
                          hide-details="auto"
                          placeholder="Select land use plan..."
                          clearable
                          :no-data-text="allAdjudicationNames.length ? 'No land use plans found for this adjudication' : 'Select a property type first'"
                        />
                      </LabelledField>
                    </v-col>
                  </v-row>

                  <!-- Subdivision: Portions -->
                  <template v-if="isSubdivision && verifiedData">
                    <v-divider class="my-5" />
                    <div class="d-flex align-center mb-1">
                      <v-icon color="primary" small class="mr-2">mdi-call-split</v-icon>
                      <span class="text-subtitle-2 font-weight-bold">Subdivision Portions</span>
                    </div>
                    <div class="text-caption mb-4 grey--text text--darken-1">
                      Sizes must add up to exactly the mother parcel size
                      (<strong>{{ formatNumber(motherSqm) }} m²</strong>).
                    </div>
                    <v-row>
                      <v-col cols="12" md="6">
                        <LabelledField label="Number of Portions" required>
                          <v-text-field
                            class="text-field-background"
                            outlined
                            dense
                            type="number"
                            v-model.number="numberOfPortions"
                            min="2"
                            hide-details="auto"
                            :rules="[v => v >= 2 || 'Must be at least 2']"
                          />
                        </LabelledField>
                      </v-col>
                      <v-col cols="12" sm="8" md="9" class="d-flex align-center flex-wrap">
                        <v-chip
                          :color="portionSizeValid ? 'success' : (portionRemaining < 0 ? 'error' : 'warning')"
                          small
                          class="mr-2 mb-1 font-weight-medium"
                        >
                          Mother: {{ formatNumber(motherSqm) }} m²
                        </v-chip>
                        <v-chip
                          :color="portionSizeValid ? 'success' : (portionRemaining < 0 ? 'error' : 'warning')"
                          small
                          class="mb-1 font-weight-medium"
                        >
                          <v-icon left x-small>{{ portionSizeValid ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                          Remaining: {{ formatNumber(portionRemaining) }} m²
                        </v-chip>
                      </v-col>
                    </v-row>
                    <v-alert
                      v-if="numberOfPortions > 20"
                      type="warning"
                      dense
                      text
                      class="mt-3"
                    >
                      Any Subdivision with more than 20 Parcels is considered comprehensive. Please consult the Physical Planning Office.
                    </v-alert>
                    <template v-if="numberOfPortions <= 20">
                    <v-simple-table dense class="mt-3">
                      <template #default>
                        <thead>
                          <tr>
                            <th style="width:80px">Portion</th>
                            <th>Size (m²)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(_, i) in portionSizes" :key="i">
                            <td class="font-weight-bold">{{ portionLabel(i) }}</td>
                            <td>
                              <v-text-field
                                class="text-field-background my-1"
                                outlined
                                dense
                                type="number"
                                v-model.number="portionSizes[i]"
                                min="0.01"
                                hide-details="auto"
                                :rules="[v => (v > 0) || 'Must be > 0']"
                                placeholder="0.00"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                    <v-row class="mt-3">
                      <v-col cols="12">
                        <v-checkbox
                          v-model="subdivAccessRoadEnabled"
                          label="Include Access Road and Other Utilities"
                          hide-details
                          class="mt-0 pt-0"
                        />
                      </v-col>
                    </v-row>
                    <v-row v-if="subdivAccessRoadEnabled" class="mt-2">
                      <v-col cols="12" md="6">
                        <LabelledField label="Access Road / Utilities Area (m²)" required>
                          <v-text-field
                            class="text-field-background"
                            outlined
                            dense
                            type="number"
                            v-model.number="subdivAccessRoadArea"
                            min="0.01"
                            hide-details="auto"
                            :rules="[v => (v > 0) || 'Must be > 0']"
                            placeholder="0.00"
                          />
                        </LabelledField>
                      </v-col>
                    </v-row>
                    </template>
                    <v-alert
                      v-if="!portionSizeValid && portionTotal > 0"
                      type="error"
                      dense
                      text
                      class="mt-3"
                    >
                      Total portions{{ subdivAccessRoadEnabled ? ' and access road/utilities' : '' }}
                      ({{ formatNumber(subdivTotalWithRoad) }} m²) must equal the mother parcel
                      ({{ formatNumber(motherSqm) }} m²). Difference: {{ formatNumber(Math.abs(portionRemaining)) }} m².
                    </v-alert>
                  </template>

                  <!-- Amalgamation: Select additional properties -->
                  <template v-if="isAmalgamation && verifiedData">
                    <v-divider class="my-5" />
                    <div class="d-flex align-center mb-1">
                      <v-icon color="primary" small class="mr-2">mdi-merge</v-icon>
                      <span class="text-subtitle-2 font-weight-bold">Properties to Amalgamate</span>
                    </div>
                    <div class="text-caption mb-4 grey--text text--darken-1">
                      Search and select additional properties belonging to the same client.
                    </div>
                    <v-row>
                      <v-col cols="12" md="7">
                        <LabelledField label="Search Additional Property (PN or LR)">
                          <v-autocomplete
                            class="text-field-background"
                            outlined
                            dense
                            v-model="amalgamSelected"
                            :items="amalgamProperties"
                            :loading="amalgamLoading"
                            :search-input.sync="amalgamSearch"
                            item-text="pn"
                            item-value="id"
                            return-object
                            no-filter
                            clearable
                            hide-details="auto"
                            placeholder="Type to search by PN…"
                            @change="addAmalgamProperty"
                          >
                            <template #item="{ item }">
                              <v-list-item-content>
                                <v-list-item-title class="caption font-weight-medium">{{ item.pn }}</v-list-item-title>
                                <v-list-item-subtitle class="caption">
                                  LR: {{ item.lr_number }} | {{ formatNumber(item.size_sqm) }} m²
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </template>
                            <template #selection><span></span></template>
                          </v-autocomplete>
                        </LabelledField>
                      </v-col>
                      <v-col cols="12" md="5" class="d-flex align-center">
                        <v-chip color="info" small class="font-weight-medium">
                          Combined Total: {{ formatNumber(amalgamTotal) }} m²
                        </v-chip>
                      </v-col>
                    </v-row>
                    <v-row v-if="amalgamAdditional.length > 0" class="mt-1">
                      <v-col cols="12">
                        <v-card flat color="grey lighten-4" class="pa-3 rounded">
                          <div class="caption grey--text text--darken-2 mb-2 font-weight-medium">
                            Selected properties ({{ amalgamAdditional.length }})
                          </div>
                          <v-chip-group column>
                            <v-chip
                              v-for="prop in amalgamAdditional"
                              :key="prop.id"
                              close
                              outlined
                              color="primary"
                              class="mr-1 mb-1"
                              @click:close="removeAmalgamProperty(prop.id)"
                            >
                              <span class="font-weight-medium">{{ prop.pn }}</span>
                              <span class="ml-1 caption grey--text"> | {{ prop.lr_number }} | {{ formatNumber(prop.size_sqm) }} m²</span>
                            </v-chip>
                          </v-chip-group>
                        </v-card>
                      </v-col>
                    </v-row>
                    <v-alert
                      v-if="!amalgamAdditional.length"
                      type="warning"
                      dense
                      text
                      class="mt-3"
                    >
                      Select at least one additional property to amalgamate.
                    </v-alert>
                    <v-row class="mt-3">
                      <v-col cols="12" md="4">
                        <LabelledField label="Resultant Property Size (m²)" required>
                          <v-text-field
                            class="text-field-background"
                            outlined
                            dense
                            type="number"
                            v-model="resultantSize"
                            min="0.01"
                            hide-details="auto"
                            :rules="[
                              v => !!v || 'Required',
                              v => parseFloat(v) > 0 || 'Must be greater than 0',
                              () => resultantSizeValid || 'Must not exceed combined total (' + formatNumber(amalgamTotal) + ' m²)',
                            ]"
                            placeholder="0.00"
                          />
                        </LabelledField>
                      </v-col>
                      <v-col cols="12" md="8" class="d-flex align-center">
                        <span class="caption grey--text text--darken-1">
                          Combined total: {{ formatNumber(amalgamTotal) }} m²
                          (Mother: {{ formatNumber(motherSqm) }} m²
                          <template v-if="amalgamAdditional.length">
                            + {{ amalgamAdditional.length }} additional
                            propert{{ amalgamAdditional.length > 1 ? 'ies' : 'y' }}
                          </template>)
                        </span>
                      </v-col>
                    </v-row>
                    <v-row class="mt-3">
                      <v-col cols="12">
                        <v-checkbox
                          v-model="amalgamAccessRoadEnabled"
                          label="Include Access Road and Other Utilities"
                          hide-details
                          class="mt-0 pt-0"
                        />
                      </v-col>
                    </v-row>
                    <v-row v-if="amalgamAccessRoadEnabled" class="mt-2">
                      <v-col cols="12" md="6">
                        <LabelledField label="Access Road / Utilities Area (m²)" required>
                          <v-text-field
                            class="text-field-background"
                            outlined
                            dense
                            type="number"
                            v-model.number="amalgamAccessRoadArea"
                            min="0.01"
                            hide-details="auto"
                            :rules="[v => (v > 0) || 'Must be > 0']"
                            placeholder="0.00"
                          />
                        </LabelledField>
                      </v-col>
                    </v-row>
                    <v-alert
                      v-if="amalgamSizeExceeded"
                      type="error"
                      dense
                      text
                      class="mt-3"
                    >
                      The resultant property size<template v-if="amalgamAccessRoadEnabled"> plus access road / utilities area
                      ({{ formatNumber(resultantSizeNum + amalgamAccessRoadAreaNum) }} m²)</template>
                      exceeds the combined total of the amalgamated properties
                      (<strong>{{ formatNumber(amalgamTotal) }} m²</strong>). Please adjust the values before proceeding.
                    </v-alert>
                  </template>

                  <v-row>
                    <v-col cols="12">
                      <LabelledField label="Remarks">
                        <v-textarea
                          class="text-field-background"
                          outlined
                          dense
                          v-model="remarks"
                          rows="2"
                          hide-details="auto"
                          placeholder="Optional remarks..."
                        />
                      </LabelledField>
                    </v-col>
                  </v-row>

                  <!-- Supporting document uploads -->
                  <template v-if="supportingDocuments.length > 0">
                    <v-divider class="my-5" />
                    <div class="d-flex align-center mb-1">
                      <v-icon color="primary" small class="mr-2">mdi-file-upload-outline</v-icon>
                      <span class="text-subtitle-2 font-weight-bold">Supporting Documents</span>
                    </div>
                    <v-alert type="info" dense text class="mb-3" icon="mdi-information-outline">
                      Only <strong>PDF files</strong> are accepted. Maximum 10 MB per document.
                    </v-alert>

                    <!-- Document table: Name | Upload | Uploaded File -->
                    <v-simple-table class="doc-upload-table">
                      <template #default>
                        <thead>
                          <tr>
                            <th style="width:35%">Document</th>
                            <th style="width:40%">Upload</th>
                            <th style="width:25%">Uploaded File</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template
                            v-for="(doc, docIdx) in supportingDocuments"
                          >
                          <tr
                            v-if="(docState[doc.id] && docState[doc.id].status === 'success') || docIdx === currentDocIndex"
                            :key="doc.id"
                          >
                            <!-- Column 1: Name + status chip -->
                            <td class="py-3">
                              <span class="font-weight-medium text-body-2">{{ doc.name }}</span>
                              <v-chip x-small color="success" dark class="ml-2"
                                v-if="docState[doc.id] && docState[doc.id].status === 'success'">
                                <v-icon x-small left>mdi-check</v-icon>Uploaded
                              </v-chip>
                              <v-chip x-small color="error" class="ml-2" v-else>
                                Required
                              </v-chip>
                            </td>

                            <!-- Column 2: Upload input -->
                            <td class="py-2">
                              <!-- Already uploaded — use Remove to replace -->
                              <template v-if="docState[doc.id] && docState[doc.id].status === 'success'">
                                <span class="caption success--text">
                                  <v-icon x-small color="success">mdi-check-circle</v-icon>
                                  Uploaded
                                </span>
                              </template>
                              <!-- Currently uploading -->
                              <template v-else-if="docState[doc.id] && docState[doc.id].status === 'uploading'">
                                <div class="d-flex align-center">
                                  <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2" />
                                  <span class="caption grey--text">Uploading…</span>
                                </div>
                              </template>
                              <!-- Active document to upload -->
                              <template v-else>
                                <v-file-input
                                  class="text-field-background doc-file-input"
                                  outlined
                                  dense
                                  placeholder="Select PDF…"
                                  prepend-icon=""
                                  prepend-inner-icon="mdi-paperclip"
                                  hide-details
                                  accept="application/pdf"
                                  truncate-length="28"
                                  :key="'fi-' + doc.id"
                                  @change="onDocumentFileSelected(doc.id, $event)"
                                />
                                <div v-if="docState[doc.id] && docState[doc.id].status === 'error'" class="caption error--text mt-1">
                                  <v-icon x-small color="error">mdi-alert-circle</v-icon>
                                  {{ docState[doc.id].message }}
                                </div>
                              </template>
                            </td>

                            <!-- Column 3: Download/Remove (uploaded) or placeholder -->
                            <td class="py-2">
                              <template v-if="docState[doc.id] && docState[doc.id].status === 'success' && docState[doc.id].appDoc">
                                <div class="d-flex align-center flex-wrap" style="gap: 6px">
                                  <v-tooltip bottom>
                                    <template #activator="{ on, attrs }">
                                      <v-btn
                                        icon
                                        x-small
                                        outlined
                                        color="grey darken-1"
                                        v-bind="attrs"
                                        v-on="on"
                                        :loading="downloadingDocId === docState[doc.id].appDoc.id"
                                        @click="onDocumentDownload(docState[doc.id].appDoc)"
                                      >
                                        <v-icon small>mdi-download</v-icon>
                                      </v-btn>
                                    </template>
                                    <span>Download</span>
                                  </v-tooltip>

                                  <v-tooltip bottom>
                                    <template #activator="{ on, attrs }">
                                      <v-btn
                                        icon
                                        x-small
                                        color="error"
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="onDocumentRemove(doc.id)"
                                      >
                                        <v-icon small>mdi-delete</v-icon>
                                      </v-btn>
                                    </template>
                                    <span>Remove</span>
                                  </v-tooltip>
                                </div>
                              </template>
                              <template v-else>
                                <span class="caption grey--text">No Upload</span>
                              </template>
                            </td>
                          </tr>
                          </template>
                        </tbody>
                      </template>
                    </v-simple-table>
                    <!-- Progress hint when more docs remain -->
                    <div
                      v-if="currentDocIndex < supportingDocuments.length"
                      class="caption grey--text text--darken-1 mt-2"
                    >
                      Document {{ currentDocIndex + 1 }} of {{ supportingDocuments.length }}
                    </div>

                    <v-alert
                      v-if="creatingDraft"
                      type="info" dense text class="mt-3"
                      icon="mdi-loading"
                    >
                      Saving draft… please wait before uploading documents.
                    </v-alert>
                  </template>

                  <v-alert
                    v-if="submitError"
                    type="error"
                    dense
                    text
                    class="mt-4"
                  >
                    <template v-if="typeof submitError === 'string'">
                      {{ submitError }}
                    </template>
                    <template v-else>
                      <div
                        v-for="(msgs, field) in submitError"
                        :key="field"
                      >
                        <div v-for="msg in msgs" :key="msg">{{ msg }}</div>
                      </div>
                    </template>
                  </v-alert>

                  <div class="mt-6 d-flex justify-space-between">
                    <v-btn
                      color="warning"
                      class="text-none"
                      :disabled="submittingDraft"
                      @click="onBackToPropertyDetails"
                    >
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                    <LoadingButton
                      color="primary"
                      :loading="submittingDraft"
                      type="submit"
                      class="text-none"
                    >
                      <v-icon left small>mdi-send</v-icon>
                      Submit Application
                      <template #loadingText>Submitting...</template>
                    </LoadingButton>
                  </div>
                  </template><!-- end v-if="!draftCreateError" -->
                </v-form>
              </LoadingArea>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Step 5: Success -->
      <v-tab-item :value="pages.SUCCESS">
        <v-row justify="center">
          <v-col cols="12" md="7" lg="6">
            <v-card class="pa-8 rounded-lg text-center" elevation="2">
              <v-avatar color="success" size="72" class="mb-4 elevation-2">
                <v-icon color="white" size="40">mdi-check</v-icon>
              </v-avatar>
              <div class="text-h6 font-weight-bold mb-2">
                Application Submitted!
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-3">
                Your scheme plan application has been received and is being processed.
              </div>
              <v-card
                v-if="createdApplication"
                flat
                color="grey lighten-4"
                class="pa-3 rounded mb-5 d-inline-block"
              >
                <span class="text-body-2 grey--text text--darken-1">Application Number</span>
                <div class="text-h6 font-weight-bold primary--text">
                  {{ createdApplication.application_number }}
                </div>
              </v-card>
              <div>
                <v-btn
                  color="primary"
                  class="text-none"
                  @click="onStartNewApplication"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  Create Another Application
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
    </template>
  </v-container>
</template>

<style scoped>
.doc-upload-table tbody tr:not(:last-child) td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.doc-file-input {
  max-width: 320px;
}
</style>
